import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useMemo, useState } from 'react'
import BefehleAlleGelesenContext from '@/features/befehlsnachrichten/hooks/useBefehleAlleGelesenContext'
import { BfnStatus } from '@/models/Befehlsnachricht'
import { Befehl } from '@repo/ui'
import BefehlCardTf from './details/BefehlCardTf'
import { useParams } from 'react-router-dom'
import { useBfnStore } from '@/store/useBfnStore'
import { useShallow } from 'zustand/react/shallow'

export interface BfnBefehleBoxProps {
  bfnStatus: BfnStatus
  befehle: Befehl[] | [] | null | undefined
  isTokenExpired: boolean
  'data-testid': string
}

const bfnStatusWhitelist = new Set([
  BfnStatus.STANDORTEINGABE_AKZEPTIERT,
  BfnStatus.ABGERUFEN,
  BfnStatus.QUITTIERT,
  BfnStatus.ERLEDIGT,
  BfnStatus.ABGEWIESEN,
  BfnStatus.WIDERRUFEN
])

export default function BfnBefehleBox({
  bfnStatus,
  befehle,
  isTokenExpired,
  'data-testid': testId
}: Readonly<BfnBefehleBoxProps>) {
  const { t } = useTranslation('befehle')
  const { setBefehleAlleGelesen } = useContext(BefehleAlleGelesenContext)
  const [gelesenIds, setGelesenIds] = useState<Array<string>>([])

  const { id: paramsBfnId } = useParams()
  const bfnDataEntry = useBfnStore(
    useShallow((state) => (paramsBfnId ? state.data[paramsBfnId] : null))
  )

  // Early validation of befehle to ensure it's a valid array
  const validBefehle = useMemo(
    () => (Array.isArray(befehle) ? befehle : []),
    [befehle]
  )

  useEffect(() => {
    if (BfnStatus.ABGERUFEN === bfnStatus) {
      setGelesenIds([])
    }
  }, [bfnStatus])

  useEffect(() => {
    if (validBefehle.length === 0) return

    const alleGelesen = validBefehle.every((befehl) => {
      return gelesenIds.includes(befehl.frontendId)
    })
    setBefehleAlleGelesen(alleGelesen)
  }, [gelesenIds, setBefehleAlleGelesen, validBefehle])

  // If bfnStatus is not in the whitelist, don't show the box
  if (!bfnStatusWhitelist.has(bfnStatus)) {
    return null
  }

  // Filter out befehle that don't have any auftraege
  const filteredBefehle = validBefehle.filter((befehl) => {
    return (
      befehl.auftraege !== null &&
      befehl.auftraege !== undefined &&
      Array.isArray(befehl.auftraege)
    )
  })

  // Return null if no valid befehle remain after filtering
  if (filteredBefehle.length === 0) {
    return null
  }

  const onBefehlGelesen = (id: string) => {
    setGelesenIds((prevIds) =>
      prevIds.includes(id) ? prevIds : [...prevIds, id]
    )
  }

  const isShowBefehlGelesenButton =
    !isTokenExpired &&
    BfnStatus.ABGERUFEN === bfnStatus &&
    !bfnDataEntry?.manuellWiderrufenAt

  const isQuttiert = !isTokenExpired && BfnStatus.QUITTIERT === bfnStatus
  const isShowBefehlAlsAbarbeitetMarkierenSlideToUnlock =
    isQuttiert && (befehle?.length ?? 0) >= 2

  return (
    <div className="w-full" data-testid={`${testId}-befehle-div`}>
      <div className="text-xs" data-testid={`${testId}-befehle-label`}>
        {t('befehle')}
      </div>
      <div
        className="flex flex-col gap-0.5"
        data-testid={`${testId}-befehl-list`}
      >
        {filteredBefehle.map((befehl, index) => {
          return (
            <BefehlCardTf
              key={befehl.frontendId}
              befehl={befehl}
              activateEmptyFieldWarnings
              data-testid={`${testId}-item-${index}`}
              onGelesen={
                isShowBefehlGelesenButton ? onBefehlGelesen : undefined
              }
              isGelesen={gelesenIds.includes(befehl.frontendId)}
              showGelesenButton={isShowBefehlGelesenButton}
              befehlsnachrichtIsQuittiert={isQuttiert}
              showBefehlAbgearbeitetSlideToUnlock={
                isShowBefehlAlsAbarbeitetMarkierenSlideToUnlock
              }
            />
          )
        })}
      </div>
    </div>
  )
}
